import clsx from 'clsx';
import React, { useState } from 'react';
import { useParams } from 'react-router';

import { ICONS } from '@/src/components/common/Elements/Icon/Icon';
import Spinner from '@/src/components/common/Elements/Spinner/Spinner';
import ModalPortal from '@/src/components/common/Modal/ModalPortal';
import { isResponseError } from '@/src/components/project/Inbox2/api/config';
import ActionButton from '@/src/components/project/Inbox2/Elements/ActionButton/ActionButton';
import { useInboxEntryPanel } from '@/src/components/project/Inbox2/store/inboxEntryPanel.store';
import { FileEntry } from '@/src/components/project/Inbox2/types';
import { showToast } from '@/src/globalFunction';

import ArchiveDocumentModal from './ArchiveDocumentModal/ArchiveDocumentModal';
import ConfirmArchiveDocumentModal from './ArchiveDocumentModal/ConfirmArchiveDocumentModal/ConfirmArchiveDocumentModal';

import { useValidateActionResponse } from '../../../../useValidateActionResponse';
import { useMarkDeleteFile } from '../../../api/markDeleteFile';

const subject = 'file';

const INITIAL_STATE = {
  showArchiveModal: false,
  showConfirmArchiveModal: false,
  archiveType: '',
};
type DeleteFileProps = {
  fileEntry: FileEntry;
  enabled: boolean;
};

const DeleteFile = ({ fileEntry, enabled }: DeleteFileProps) => {
  const [state, setState] = useState(INITIAL_STATE);
  const { showConfirmArchiveModal, showArchiveModal, archiveType } = state;
  const { setInboxEntry } = useInboxEntryPanel();
  const { projectId } = useParams<{ projectId: string }>();
  // Bind this to container if delete is enabled for email entries
  const deleteFileMutation = useMarkDeleteFile({
    projectId,
    sourceFileId: fileEntry.id,
    trash: true,
  });
  const { confirmRefreshModal } = useValidateActionResponse({
    responseError: isResponseError(deleteFileMutation.data) ? deleteFileMutation.data : null,
  });
  const hasDeleteOptions = fileEntry.taskList?.length || fileEntry.isArchivedInLibrary;

  const handleClick = () => {
    if (!hasDeleteOptions) {
      setState((prevState) => ({
        ...prevState,
        showConfirmArchiveModal: true,
      }));
    } else {
      setState((prevState) => ({
        ...prevState,
        showArchiveModal: true,
      }));
    }
  };

  const handleClose = () => {
    setState({ ...INITIAL_STATE });
  };

  const handleDelete = async () => {
    const response = await deleteFileMutation.mutateAsync({
      trash: true,
      fileId: fileEntry.id,
    });

    if (response) {
      handleClose();

      setInboxEntry(null);
      showToast('success', 'Successfully deleted the entry.');
    }
  };

  const handleRadioChange = (e) => {
    const archiveType = e.target.value;

    setState((prevState) => ({
      ...prevState,
      archiveType,
    }));
  };

  return (
    <React.Fragment>
      {deleteFileMutation.isLoading && <Spinner />}
      <ActionButton
        id={'delete_file'}
        className={clsx('tw-text-cfo-red-400', 'hover:tw-bg-red-100')}
        icon={ICONS.delete}
        iconClasses={clsx('tw-text-cfo-red-400', 'group-hover:tw-text-cfo-red-400')}
        onClick={handleClick}
        tooltipSettings={{
          content: 'Delete',
          place: 'left',
        }}
        disabled={!enabled}
      />

      {showArchiveModal && (
        <ModalPortal>
          <ArchiveDocumentModal
            onClose={handleClose}
            onRadioChange={handleRadioChange}
            onArchive={handleDelete}
            isArchivedtoLibrary={fileEntry.isArchivedInLibrary}
            hasTasks={fileEntry.taskList?.length > 0}
            selected={archiveType}
            subject={subject}
          />
        </ModalPortal>
      )}
      {showConfirmArchiveModal && (
        <ModalPortal>
          <ConfirmArchiveDocumentModal
            subject={subject}
            bodySubject={subject}
            onClose={handleClose}
            onSubmit={handleDelete}
          />
        </ModalPortal>
      )}
      {confirmRefreshModal}
    </React.Fragment>
  );
};

export default DeleteFile;
